import type { ComponentType } from 'react';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

/**
 * Replacement for tss-react's `withStyles` for styles that do not read props (every v4-converted
 * styles function — v4 passed only the theme, so none of them can). It preserves v4's contract,
 * not tss's whole surface: unlike tss's wrapper it never merges `classes.root` into the child's
 * `className`, so root-only styles reach only components that consume the `classes` prop — which
 * is exactly how v4 behaved for these sites.
 *
 * tss-react's own `withStyles` calls its generated hook as `useStyles(props, { props })`: the
 * whole props object becomes the style params, and the styles memo is keyed on the params
 * reference — unstable whenever any prop is a function or object, i.e. on effectively every
 * render of every real component. That re-serializes the full styles object (148 rules for the
 * shared sheet) per render, where v4 serialized once per component type.
 *
 * Calling the hook with no params keeps the memo stable across renders; the `classes` prop is
 * still merged into the generated classes via the second argument, preserving tss's (and v4's)
 * override contract.
 */
// Loose return type on purpose: every wrapped component is an untyped .jsx module, where TS infers
// all destructured props as required — propagating that to consumers would demand props that are
// genuinely optional. Matches the ergonomics these components had under v4's withStyles.
export function withStyles<P>(
  Component: ComponentType<P>,
  stylesFnOrObject: unknown
): ComponentType<Record<string, unknown>> {
  const displayName = Component.displayName || Component.name || 'Component';
  // name gives generated class names a readable prefix. It must stay CSS-ident-safe: the name
  // becomes part of the emitted class selector, and one illegal character (e.g. the parens of an
  // HOC displayName) makes browsers discard the whole rule. Dev-readability only — the production
  // minifier mangles function names (no esbuild keepNames), so bundles ship a short prefix.
  // The styles come from untyped .js modules; tss validates the shape at runtime.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const useStyles = makeStyles({ name: `WithStyles-${displayName.replace(/[^\w-]/g, '-')}` })(stylesFnOrObject as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Inner = Component as any;
  const WithStyles = React.forwardRef((props, ref) => {
    const { classes } = useStyles(undefined, { props: props as { classes?: Record<string, string> } });
    return <Inner ref={ref} {...props} classes={classes} />;
  });
  WithStyles.displayName = `WithStyles(${displayName})`;
  return WithStyles as unknown as ComponentType<Record<string, unknown>>;
}
